import { ArrowSquareOut, ChatCircle } from "@phosphor-icons/react";
import { Node } from "@tiptap/core";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

/**
 * A conversation embedded in a document — a link-card to a thread, not the thread itself.
 *
 * **Render and round-trip only in this wave.** Creating one needs a thought id from the
 * conversations layer that has not landed yet, so the node parses, renders, serialises, and
 * emits a host callback on click; the host says so plainly rather than opening something that
 * does not exist.
 *
 * The participant lists are JSON STRINGS on the node, because ProseMirror/Yjs attrs are
 * scalars — and because an object attr is re-written into the CRDT on every transaction. They
 * are parsed back into arrays exactly once, at the boundary where a host receives them.
 */

export interface ConversationClick {
  conversationId: string;
  title: string;
  source: "conversation";
  participantUserIds: string[];
  participantThinkerIds: string[];
}

export interface ConversationOptions {
  /** Fired on click. The host decides what opening a conversation means. */
  onOpenConversation?: (info: ConversationClick) => void;
  /** Read-only renders an external-link glyph instead of an in-place affordance. */
  readOnly?: boolean;
}

function parseIdList(raw: unknown): string[] {
  if (typeof raw !== "string" || raw.trim().length === 0) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function ConversationCard(props: NodeViewProps) {
  const attrs = props.node.attrs as {
    conversationId: string;
    title: string;
    participantUserIds: string;
    participantThinkerIds: string;
  };
  const options = props.extension.options as ConversationOptions;
  const users = parseIdList(attrs.participantUserIds);
  const thinkers = parseIdList(attrs.participantThinkerIds);
  const count = users.length + thinkers.length;

  return (
    <NodeViewWrapper as="div" contentEditable={false}>
      <button
        type="button"
        onClick={() =>
          options.onOpenConversation?.({
            conversationId: attrs.conversationId,
            title: attrs.title,
            source: "conversation",
            participantUserIds: users,
            participantThinkerIds: thinkers,
          })
        }
        className="flex w-full items-center gap-2 rounded-md border border-divider bg-bg-secondary px-3 py-2 text-start hover:bg-hover-bg"
      >
        <ChatCircle size={16} aria-hidden className="text-text-tertiary" />
        <span className="min-w-0 flex-1 truncate text-body text-text">{attrs.title}</span>
        <span className="text-small text-text-tertiary">{count}</span>
        {options.readOnly ? (
          <ArrowSquareOut size={14} aria-hidden className="text-text-tertiary rtl:rotate-180" />
        ) : null}
      </button>
    </NodeViewWrapper>
  );
}

export const ConversationNode = Node.create<ConversationOptions>({
  name: "conversation",
  group: "block",
  atom: true,
  defining: true,
  draggable: true,

  addOptions() {
    return {};
  },

  addAttributes() {
    return {
      conversationId: { default: "" },
      title: { default: "" },
      // JSON strings, not arrays — see the note at the top of the file.
      participantUserIds: { default: "[]" },
      participantThinkerIds: { default: "[]" },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-conversation-id]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      {
        "data-conversation-id": String(HTMLAttributes.conversationId ?? ""),
        "data-title": String(HTMLAttributes.title ?? ""),
      },
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ConversationCard);
  },

  renderMarkdown(node) {
    const attrs = (node.attrs ?? {}) as { conversationId?: string; title?: string };
    return `[${attrs.title ?? ""}](conversation://${attrs.conversationId ?? ""})`;
  },
});
